import { isLegacyApi, sortApiObjects } from '@nordcraft/core/dist/api/api'
import type {
  ComponentData,
  ComponentFormula,
  ComponentNodeModel,
  ComponentVariable,
  SupportedNamespaces,
} from '@nordcraft/core/dist/component/component.types'
import { applyFormula } from '@nordcraft/core/dist/formula/formula'
import { appendUnit } from '@nordcraft/core/dist/styling/customProperty'
import type { Nullable } from '@nordcraft/core/dist/types'
import { filterObject, mapObject } from '@nordcraft/core/dist/utils/collections'
import { getNodeSelector } from '@nordcraft/core/dist/utils/getNodeSelector'
import { isDefined } from '@nordcraft/core/dist/utils/util'
import type { ComponentAPI } from '@nordcraft/core/src/api/apiTypes'
import { isContextApiV2 } from '../api/apiUtils'
import { createLegacyAPI } from '../api/createAPI'
import { createAPI } from '../api/createAPIv2'
import { isContextProvider } from '../context/isContextProvider'
import { subscribeToContext } from '../context/subscribeToContext'
import { registerComponentToLogState } from '../debug/logState'
import { handleAction } from '../events/handleAction'
import type { Signal } from '../signal/signal'
import { signal } from '../signal/signal'
import type { ComponentChild, ComponentContext, ContextApi } from '../types'
import { createFormulaCache } from '../utils/createFormulaCache'
import { formulaHasValue } from '../utils/formulaHasValue'
import { getComponent } from '../utils/getComponent'
import { subscribeCustomProperty } from '../utils/subscribeCustomProperty'
import { renderComponent } from './renderComponent'

export type RenderComponentNodeProps = {
  path: string
  node: ComponentNodeModel
  dataSignal: Signal<ComponentData>
  ctx: ComponentContext
  parentElement: Element | ShadowRoot
  instance: Record<string, string>
  namespace?: SupportedNamespaces
}

export function createComponent({
  node,
  path,
  dataSignal,
  ctx,
  parentElement,
  instance,
  namespace,
}: RenderComponentNodeProps): ReadonlyArray<Element | Text> {
  const nodeLookupKey = [ctx.package, node.name].filter(isDefined).join('/')
  const component = getComponent(
    nodeLookupKey,
    ctx.components,
    ctx.env.runtime !== 'preview',
  )
  if (!component) {
    // eslint-disable-next-line no-console
    console.warn(
      `Could not find component "${nodeLookupKey}" for component "${
        ctx.component.name
      }". Available components are: ["${ctx.components
        .map((c) => c.name)
        .join('", "')}"]`,
    )
    return []
  }
  const formulaCtx = {
    component: ctx.component,
    formulaCache: ctx.formulaCache,
    root: ctx.root,
    package: ctx.package,
    toddle: ctx.toddle,
    env: ctx.env,
    reportFormulaEvaluation: ctx.reportFormulaEvaluation,
  }
  const attributesSignal = dataSignal.map((data) => {
    return mapObject(node.attrs ?? {}, ([attr, value]) => [
      attr,
      value?.type !== 'value'
        ? applyFormula(
            value,
            {
              ...formulaCtx,
              data,
            },
            ['attrs', attr],
          )
        : value?.value,
    ])
  })

  const componentDataSignal = signal<ComponentData>({
    Location: dataSignal.get().Location,
    Attributes: attributesSignal.get(),
    Apis: mapObject(
      filterObject(component.apis ?? {}, ([_, api]) => isDefined(api)),
      ([name, api]) => [
        name,
        {
          data: null,
          isLoading:
            api!.autoFetch &&
            applyFormula(
              api!.autoFetch,
              {
                ...formulaCtx,
                component,
                data: dataSignal.get(),
              },
              ['apis', name, 'autoFetch'],
            )
              ? true
              : false,
          error: null,
        },
      ],
    ),
  })

  // Subscribe to global stores (currently only theme)
  // We subscribe before calculating variable initial values to ensure they can reference global store values
  ctx.stores.theme.subscribe((newTheme) => {
    componentDataSignal.update((data) => ({
      ...data,
      Page: {
        ...data.Page,
        Theme: newTheme,
      },
    }))
  })

  // Subscribe context before calculating variable initial values to ensure they can reference context values
  subscribeToContext(componentDataSignal, component, ctx)
  componentDataSignal.update((data) => ({
    ...data,
    Variables: mapObject(
      filterObject<Nullable<ComponentVariable>, ComponentVariable>(
        component.variables ?? {},
        ([_, variable]) => isDefined(variable),
      ),
      ([name, variable]) => [
        name,
        applyFormula(
          variable.initialValue,
          {
            // Initial value
            ...formulaCtx,
            component,
            data: componentDataSignal.get(),
          },
          ['variables', name],
        ),
      ],
    ),
  }))
  registerComponentToLogState(component, componentDataSignal)

  // Call the abort signal if the component's datasignal is destroyed (component unmounted) to cancel any pending requests
  const abortController = new AbortController()
  componentDataSignal.subscribe(
    (data) => {
      Object.entries(data.Variables ?? {}).forEach(([name, value]) => {
        ctx.reportFormulaEvaluation?.(['variables', name], value, ctx)
      })
    },
    {
      destroy: () =>
        abortController.abort(`Component ${component.name} unmounted`),
    },
  )
  const formulaCache = createFormulaCache(component)

  // Note: this function must run procedurally to ensure apis (which are in correct order) can reference each other
  const apis: Record<string, ContextApi> = {}
  sortApiObjects(
    Object.entries(component.apis ?? {}).filter(
      (entry): entry is [string, ComponentAPI] => isDefined(entry[1]),
    ),
  ).forEach(([name, api]) => {
    if (isLegacyApi(api)) {
      apis[name] = createLegacyAPI(api, {
        ...ctx,
        apis,
        component,
        dataSignal: componentDataSignal,
        abortSignal: abortController.signal,
        isRootComponent: false,
        formulaCache,
        package: node.package ?? ctx.package,
        triggerEvent: (eventTrigger, data) => {
          const eventHandler = Object.values(node.events ?? {}).find(
            (e) => e?.trigger === eventTrigger,
          )
          if (eventHandler) {
            eventHandler.actions?.forEach((action) =>
              handleAction(action, { ...dataSignal.get(), Event: data }, ctx),
            )
          }
        },
      })
    } else {
      apis[name] = createAPI({
        apiRequest: api,
        ctx: {
          ...ctx,
          apis,
          component,
          dataSignal: componentDataSignal,
          abortSignal: abortController.signal,
          isRootComponent: false,
          formulaCache,
          package: node.package ?? ctx.package,
          triggerEvent: (eventTrigger, data) => {
            const eventHandler = Object.values(node.events ?? {}).find(
              (e) => e?.trigger === eventTrigger,
            )
            if (eventHandler) {
              eventHandler.actions?.forEach((action) =>
                handleAction(action, { ...dataSignal.get(), Event: data }, ctx),
              )
            }
          },
        },
        componentData: componentDataSignal.get(),
      })
    }
  })
  Object.values(apis)
    .filter(isContextApiV2)
    .forEach((api) => {
      api.triggerActions(componentDataSignal.get())
    })

  const onEvent = (eventTrigger: string, data: any) => {
    const eventHandler = Object.values(node.events ?? {}).find(
      (e) => e?.trigger === eventTrigger,
    )
    if (eventHandler) {
      eventHandler.actions?.forEach((action) =>
        handleAction(action, { ...dataSignal.get(), Event: data }, ctx),
      )
    }
  }

  let providers = ctx.providers
  if (isContextProvider(component)) {
    // Subscribe to exposed formulas and update the component's data signal
    const formulaDataSignals = Object.fromEntries(
      Object.entries(component.formulas ?? {})
        .filter(([, formula]) => formula?.exposeInContext)
        .map(([name, formula]) => [
          name,
          componentDataSignal.map((data) =>
            applyFormula(
              (formula as ComponentFormula).formula,
              {
                data,
                component,
                formulaCache: ctx.formulaCache,
                root: ctx.root,
                package: ctx.package,
                toddle: ctx.toddle,
                env: ctx.env,
                jsonPath: ctx.jsonPath,
                reportFormulaEvaluation: ctx.reportFormulaEvaluation,
              },
              ['formulas', name],
            ),
          ),
        ]),
    )

    providers = {
      ...providers,
      [component.name]: {
        component,
        formulaDataSignals,
        ctx: {
          ...ctx,
          apis,
          component,
          dataSignal: componentDataSignal,
          abortSignal: abortController.signal,
          triggerEvent: onEvent,
        },
      },
    }
  }

  const children: Record<string, Array<ComponentChild>> = {}
  for (let i = 0; i < (node?.children ?? []).length; i++) {
    const childId = node.children?.[i]
    if (childId === undefined) {
      continue
    }
    const childNode = ctx.component.nodes?.[childId]
    const slotName = childNode?.slot ?? 'default'
    children[slotName] = children[slotName] ?? []
    children[slotName].push({
      id: childId,
      path: `${path}.${i}[${slotName}]`,
      dataSignal,
      ctx: {
        ...ctx,
        package: node.package ?? ctx.package,
      },
    })
  }

  attributesSignal.subscribe(
    (Attributes) =>
      componentDataSignal.update((data) => ({
        ...data,
        Attributes,
      })),
    { destroy: () => componentDataSignal.destroy() },
  )

  const renderedComponent = renderComponent({
    dataSignal: componentDataSignal,
    component,
    components: ctx.components,
    path,
    root: ctx.root,
    isRootComponent: false,
    children,
    formulaCache,
    providers,
    stores: ctx.stores,
    apis,
    abortSignal: abortController.signal,
    package: node.package ?? ctx.package,
    parentElement,
    onEvent,
    toddle: ctx.toddle,
    env: ctx.env,
    namespace,
    // If the root node is another component, then append and forward previous instance
    instance:
      node.id === 'root'
        ? { ...instance, [ctx.component.name]: 'root' }
        : { [ctx.component.name]: node.id ?? '' },
    jsonPath: ctx.jsonPath,
    reportFormulaEvaluation: ctx.reportFormulaEvaluation,
  })

  // Custom properties instance overrides are added after the child tree is rendered to ensure correct order
  Object.entries(node.customProperties ?? {})
    .filter(([_, { formula }]) => formulaHasValue(formula))
    .forEach(([customPropertyName, customProperty]) =>
      subscribeCustomProperty({
        selector: getNodeSelector(path, {
          componentName: ctx.component.name,
          nodeId: node.id,
        }),
        signal: dataSignal.map((data) =>
          appendUnit(
            applyFormula(
              customProperty.formula,
              {
                ...formulaCtx,
                data,
              },
              ['customProperties', customPropertyName, 'formula'],
            ),
            customProperty.unit,
          ),
        ),
        customPropertyName,
        root: ctx.root,
      }),
    )
  node.variants?.forEach((variant) => {
    Object.entries(variant.customProperties ?? {})
      .filter(([_, { formula }]) => formulaHasValue(formula))
      .forEach(([customPropertyName, customProperty]) =>
        subscribeCustomProperty({
          selector: getNodeSelector(path, {
            componentName: ctx.component.name,
            nodeId: node.id,
            variant,
          }),
          signal: dataSignal.map((data) =>
            appendUnit(
              applyFormula(
                customProperty.formula,
                {
                  ...formulaCtx,
                  data,
                },
                ['customProperties', customPropertyName, 'formula'],
              ),
              customProperty.unit,
            ),
          ),
          customPropertyName,
          variant,
          root: ctx.root,
        }),
      )
  })

  return renderedComponent
}
